import { FormNumberInput } from '@components/forms/number/base/FormNumberInput';
import { Grid, Button, Tooltip } from '@mui/material';
import { useState } from 'react';
import { validatePin } from '@utils/validate';
import { PIN_LENGTH } from '@consts/location';
import { LockOpen, Lock } from '@mui/icons-material';
import { useStyles } from './styles/PinInput.styles';

import type { PinnedEntity } from '@/types/locations';

type PinInputProps<T extends PinnedEntity> = {
    draft: [T, React.Dispatch<T>];
    showLock?: boolean;
    disabled?: boolean;
};

export const generateRandomPin = () => (Math.floor(Math.random() * (9999 - 1e3)) + 1e3).toString();

export const PinInput = <T extends PinnedEntity>({ draft, showLock, disabled }: PinInputProps<T>) => {
    const [draftState, setDraftState] = draft;
    const [locked, setLocked] = useState(true);
    const classes = useStyles();

    const handleLockClick = () => {
        setLocked((prev) => {
            if (!prev && !validatePin(draftState.PIN)) return prev;
            return !prev;
        });
    };

    return (
        <>
            <FormNumberInput
                disabled={showLock ? locked : false}
                required
                size={showLock ? 7 : 8}
                draft={draft}
                stateKey='PIN'
                label='Pin'
                helperText={{
                    valid: '',
                    invalid: `Please provide a valid ${PIN_LENGTH} digit pin not beginning with zero.`,
                }}
                maxLength={PIN_LENGTH}
                validateFn={validatePin}
            />
            <Grid item xs={4} sm={4} md={4} display='flex' justifyContent='center' alignItems='center'>
                <Button
                    disabled={disabled || showLock ? locked : false}
                    type='button'
                    size={'large'}
                    variant={'outlined'}
                    color={'secondary'}
                    onClick={() => {
                        setDraftState({
                            ...draftState,
                            PIN: generateRandomPin(),
                        });
                    }}>
                    Generate
                </Button>
            </Grid>
            {showLock && (
                <Grid item xs={1} sm={1} md={1} display='flex' justifyContent='center' alignItems='center'>
                    <Tooltip title={locked ? 'Unlock to manage pin' : 'Lock pin changes'}>
                        <Button onClick={handleLockClick} color='inherit' disableRipple disabled={disabled}>
                            {locked ? <Lock className={classes.pointer} /> : <LockOpen className={classes.pointer} />}
                        </Button>
                    </Tooltip>
                </Grid>
            )}
        </>
    );
};
